import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalizarNome(nome?: string): string {
  if (!nome) return "-";
  return nome.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Normaliza texto para busca: minúsculas e sem acentos/diacríticos
 * ("Agrônomo" → "agronomo"), para que "agro" encontre "Agrônomo".
 * NFD separa a letra do acento; a faixa U+0300–U+036F são os acentos soltos.
 */
const SEM_DIACRITICOS = new RegExp("[\\u0300-\\u036f]", "g");
export function normalizarBusca(texto: string): string {
  return texto.toLowerCase().normalize("NFD").replace(SEM_DIACRITICOS, "");
}

// Regra de negócio, espelhando a RLS `fichas_update`: edita quem LANÇOU a
// ficha, qualquer que seja o papel, mais gestor e master — e o administrativo
// enquanto a ficha ainda NÃO foi lançada.
//
// Virou ALLOWLIST em 01/09/2026 (IGO-153). Era `role !== "vendedor"`, uma
// denylist de um item, e isso tinha dois problemas: qualquer papel novo passaria
// por omissão, e o `administrativo` passava para QUALQUER ficha, inclusive já
// lançada.
//
// Mas a allowlist tirou o administrativo por completo, e isso foi longe demais:
// ele lança ficha em nome de outro vendedor da unidade, e isso é um UPDATE onde
// `userId !== fichaVendedorId` por construção. O recurso ficou 20 dias quebrado
// em produção — o banco recusava, e o front ainda no ar (com a denylist antiga)
// oferecia o botão assim mesmo, então o erro chegava ao usuário como
// "Não foi possível salvar a ficha".
//
// Daí o 4º parâmetro. `jaLancada` tem default `true`, que é o conservador: sem
// a informação, o administrativo NÃO edita. Quem conhece o status passa o valor.
// Espelha o USING da policy `fichas_update` (migration 20260921120000).
export function podeEditarFicha(
  role: string | null | undefined,
  userId: string | null | undefined,
  fichaVendedorId: string | null | undefined,
  jaLancada: boolean = true
): boolean {
  if (role === "gestor" || role === "master") return true;
  if (role === "administrativo" && !jaLancada) return true;
  return !!userId && userId === fichaVendedorId;
}

// Rótulo do botão principal de salvar da ficha: "Lançar Ficha" no primeiro
// salvamento (ficha ainda pendente/erro) e "Atualizar Ficha" do segundo em diante.
export function rotuloBotaoFicha(jaLancada: boolean): "Lançar Ficha" | "Atualizar Ficha" {
  return jaLancada ? "Atualizar Ficha" : "Lançar Ficha";
}

// Normaliza telefone para o formato 55xx9xxxxxxxx (DDI + DDD + 9 dígitos).
// Aceita entradas com máscara, sem DDD, sem DDI ou sem o nono dígito.
// Retorna null se não conseguir normalizar.
const DEFAULT_DDD = '44'; // Maringá

export function normalizarTelefone(raw?: string | null): string | null {
  if (!raw) return null;
  let d = String(raw).replace(/\D+/g, '');
  if (!d) return null;

  if (d.startsWith('55')) {
    const rest = d.slice(2);
    if (rest.length < 10) return null;
    const ddd = rest.slice(0, 2);
    let local = rest.slice(2);
    if (local.length === 8) local = '9' + local;
    if (local.length > 9) local = local.slice(-9);
    return `55${ddd}${local}`;
  }

  if (d.length === 11) return `55${d}`;

  if (d.length === 10) {
    const ddd = d.slice(0, 2);
    const local = '9' + d.slice(2);
    return `55${ddd}${local}`;
  }

  if (d.length === 8) d = '9' + d;
  if (d.length === 9) return `55${DEFAULT_DDD}${d}`;

  return null;
}

// Formata telefone para exibição: (xx) 9 xxxx-xxxx (ou (xx) xxxx-xxxx para fixo).
// Retorna "-" para placeholder em listas/cards. Use formatarTelefoneInput para inputs (retorna "" quando vazio).
export function formatarTelefone(telefone?: string): string {
  if (!telefone) return "-";
  return formatarTelefoneInput(telefone) || "-";
}

// Versão para inputs: retorna "" quando vazio para não bagunçar o campo.
export function formatarTelefoneInput(telefone?: string): string {
  if (!telefone) return "";

  const numeros = telefone.replace(/\D/g, '');

  // 13 dígitos com DDI: 55 + DDD + 9 + 4 + 4 → (44) 9 9999-8888
  if (numeros.length === 13 && numeros.startsWith('55')) {
    const ddd = numeros.slice(2, 4);
    const nono = numeros.slice(4, 5);
    const parte1 = numeros.slice(5, 9);
    const parte2 = numeros.slice(9, 13);
    return `(${ddd}) ${nono} ${parte1}-${parte2}`;
  }
  // 11 dígitos sem DDI: DDD + 9 + 4 + 4 → (44) 9 9999-8888
  if (numeros.length === 11) {
    const ddd = numeros.slice(0, 2);
    const nono = numeros.slice(2, 3);
    const parte1 = numeros.slice(3, 7);
    const parte2 = numeros.slice(7, 11);
    return `(${ddd}) ${nono} ${parte1}-${parte2}`;
  }
  // 10 dígitos sem DDI (fixo): DDD + 4 + 4 → (44) 9999-8888
  if (numeros.length === 10) {
    const ddd = numeros.slice(0, 2);
    const parte1 = numeros.slice(2, 6);
    const parte2 = numeros.slice(6, 10);
    return `(${ddd}) ${parte1}-${parte2}`;
  }

  // Não bate nenhum formato — devolve como veio para o usuário poder corrigir
  return telefone;
}

// Converte string de data "yyyy-MM-dd" para Date object sem problemas de timezone
// Garante que a data exibida seja exatamente a mesma salva no banco
export function parseDataSemFuso(dataString?: string): Date | undefined {
  if (!dataString) return undefined;
  
  // Se for formato yyyy-MM-dd, cria Date com hora local (não UTC)
  const [ano, mes, dia] = dataString.split('-').map(Number);
  if (ano && mes && dia) {
    return new Date(ano, mes - 1, dia); // mes-1 porque JS começa em 0
  }
  
  return undefined;
}

// Formata Date para string "yyyy-MM-dd" sem problemas de timezone
export function formatarDataParaBanco(data?: Date): string | null {
  if (!data) return null;
  
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  
  return `${ano}-${mes}-${dia}`;
}
